import React, { useEffect, useState, useContext } from "react";
import { useSnackbar } from "notistack";
import httpRequest from "../../httpRequest";
import { Grid, Typography, CircularProgress, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box, Modal, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { Card, CardHeader } from '@ui5/webcomponents-react';
import { DataGrid } from '@mui/x-data-grid';
import AppLayout from "../../components/AppLayout/AppLayout";
import ReleaseIcon from '@mui/icons-material/CheckCircle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AttachmentIcon from '@mui/icons-material/Attachment';
import ButtonContained from "../../components/StyledComponents/ButtonContained";
import ButtonOutlined from "../../components/StyledComponents/ButtonOutlined";
import StyledTextField from "../../components/StyledComponents/StyledTextField";
import UserContext from "../../context/user-context";

const ExpenseReimbursements = () => {
  const { enqueueSnackbar } = useSnackbar();
  const userCtx = useContext(UserContext); // Access user context

  const [currentUserPositionId, setCurrentUserPositionId] = useState(null);
  const [hierarchies, setHierarchies] = useState([]);
  const [employeeCode, setEmployeeCode] = useState('');
  const [currentUserId, setCurrentUserId] = useState('');
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedParentExpense, setSelectedParentExpense] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedChildExpense, setSelectedChildExpense] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Set user-related data from context
        setEmployeeCode(userCtx.employeeCode);
        setCurrentUserId(userCtx.userId);
        setCurrentUserPositionId(userCtx.employeeData.position_id);

        // Fetch expense approval hierarchies for the current user's position ID
        const hierarchiesResponse = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-expense-hierarchies?hierarchy_position=${userCtx.employeeData.position_id}`);
        setHierarchies(hierarchiesResponse.data.hierarchies);

        // Fetch all necessary data
        await fetchExpenses(userCtx.employeeCode);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data.");
        enqueueSnackbar("Error fetching data", { variant: "error" });
      }
    };

    fetchData();
  }, [userCtx, enqueueSnackbar]); // Added userCtx as dependency to rerun effect when user context changes.

  const fetchExpenses = async (employeeCode) => {
    try {
      setLoading(true);

      // Fetch the parent expenses for the approver
      const expenseResponse = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-expenses?standing_employee_code=${employeeCode}&exclude_fields=expenses`);
      const parentExpenses = expenseResponse.data.expenses;

      // Directly use the fetched data since it already includes company_name, employee_name, etc.
      const enrichedParentExpenses = parentExpenses.map(expense => ({
        ...expense,
        expense_submit_date: expense.expense_submit_date
          ? new Date(expense.expense_submit_date).toLocaleDateString('en-GB') // Format: DD.MM.YYYY
          : "N/A", // Fallback if date is missing
      }));

      setExpenses(enrichedParentExpenses);
    } catch (error) {
      console.error("Error fetching parent expenses:", error);
      setError("Error fetching parent expenses");
    } finally {
      setLoading(false);
    }
  };
  

const handleOpenFile = (fileData, fileType) => {
  const byteCharacters = atob(fileData); // Decode base64
  const byteNumbers = new Array(byteCharacters.length).fill().map((_, i) => byteCharacters.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: fileType });

  const blobUrl = URL.createObjectURL(blob);
  const windowFeatures = "width=600,height=800";

  if (fileType === 'application/pdf') {
    window.open(blobUrl, "_blank", windowFeatures);
  } else if (fileType.startsWith('image')) {
    window.open(blobUrl, "_blank", windowFeatures);
  }
};


const handleRelease = async (parentExpenseId) => {
  try {
    // Find the selected parent expense from the state
    const parentExpense = expenses.find(expense => expense.id === parentExpenseId);

    if (!parentExpense) {
      enqueueSnackbar("Parent expense not found", { variant: "error" });
      return;
    }

    // Check if the current user is the approver for the expense's position ID
    const hierarchy = hierarchies.find(h => h.position_id === parentExpense.position_id);
    let snackbarMessage = "Expense released successfully";
    if (hierarchy && hierarchy.hierarchy_positions[hierarchy.hierarchy_positions.length - 1] === currentUserPositionId) {
      snackbarMessage = "Expense approved successfully";
    }

    // Include user_id in the release request payload
    const payload = {
      user_id: currentUserId,
    };

    console.log(payload);

    // Proceed with releasing the expense
    await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/release-expense/${parentExpenseId}`, payload);
    enqueueSnackbar(snackbarMessage, { variant: "success" });
    await fetchExpenses(employeeCode); // Refresh expenses list
  } catch (error) {
    console.error("Error releasing expense:", error);
    enqueueSnackbar("Error releasing expense", { variant: "error" });
  }
};



const handleOpenModal = async (expense) => {
  try {
    // Fetch the child expenses for the selected parent expense
    const childExpenseResponse = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-expenses?expense_id=${expense.id}`);
    const childExpenses = childExpenseResponse.data.expenses[0].expenses;

    // Fetch the categories for the child expenses
    const categoryResponse = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/categories`);
    const categories = categoryResponse.data.categories;

    // Map the child expenses to include the category name
    const enrichedChildExpenses = childExpenses.map(childExpense => {
      const category = categories.find(cat => cat.id === childExpense.expense_category_id);
      return {
        ...childExpense,
        expense_category_name: category ? category.expense_category : "Unknown"
      };
    });

    // Update the selected parent expense with the enriched child expenses
    setSelectedParentExpense({
      ...expense,
      expenses: enrichedChildExpenses
    });

    setModalOpen(true);
  } catch (error) {
    console.error("Error fetching child expenses:", error);
    enqueueSnackbar("Error fetching child expenses", { variant: "error" });
  }
};


const handleCloseModal = () => {
  setModalOpen(false);
  setSelectedParentExpense(null);
};

// Open Reject Dialog
const handleOpenRejectDialog = (childExpense) => {
  setSelectedChildExpense(childExpense);
  setRejectDialogOpen(true);
};

// Close Reject Dialog
const handleCloseRejectDialog = () => {
  setRejectDialogOpen(false);
  setSelectedChildExpense(null);
  setRejectReason('');
};
const handleRejectExpense = async () => {
  try {
    const payload = {
      is_rejected: true,
      reject_reason: rejectReason,
      employee_code: employeeCode,
    };

    await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/reject-expense/${selectedChildExpense.id}`, payload);
    enqueueSnackbar('Expense rejected successfully', { variant: 'success' });
    setRejectDialogOpen(false);
    handleCloseModal();
    setSelectedChildExpense(null);
    setRejectReason('');
    fetchExpenses(employeeCode); // Refresh the data
    
  } catch (error) {
    console.error('Error rejecting expense:', error);
    enqueueSnackbar('Error rejecting expense', { variant: 'error' });
  }
};



  const handleSearch = (event) => {
    setSearch(event.target.value.toLowerCase());
  };

  const filteredParentExpenses = expenses.filter(expense =>
    expense.employee_name.toLowerCase().includes(search) ||
    expense.position_name.toLowerCase().includes(search) ||
    expense.company_name.toLowerCase().includes(search) ||
    expense.function_name.toLowerCase().includes(search)
  );

  const numberOfPendingExpenses = filteredParentExpenses.length;

  const columns = [
    { field: 'expense_submit_date', headerName: 'Expense Submit Date', flex: 1 },
    { field: 'month_year', headerName: 'Month Year', flex: 1 },
    { field: 'employee_code', headerName: 'Employee Code', flex: 1 },
    { field: 'employee_name', headerName: 'Employee Name', flex: 1 },
    { field: 'position_name', headerName: 'Position', flex: 1 },
    { field: 'company_name', headerName: 'Company', flex: 1 },
    { field: 'function_name', headerName: 'Function', flex: 1 },
    { 
      field: 'total_base_amount', 
      headerName: 'Amount', 
      flex: 1,
      valueFormatter: (params) => {
        // Ensure total_base_amount is a number before formatting
        const value = params.value;
        return value ? value.toLocaleString() : '';
      }
    },
    { field: 'base_currency', headerName: 'Base Currency', flex: 1 },
    
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250,
      renderCell: (params) => {
        const expensePositionId = params.row.position_id;
        let buttonText = "Release";
      
        // Find the hierarchy for the expense's position ID
        const hierarchy = hierarchies.find(h => h.position_id === expensePositionId);
      
        if (hierarchy) {
          // Check if the current user is the approver for the expense's position ID
          const currentUserIndex = hierarchy.hierarchy_positions.indexOf(currentUserPositionId);
          if (currentUserIndex !== -1 && currentUserIndex === hierarchy.hierarchy_positions.length - 1) {
            buttonText = "Approve";
          }
        }
      
        return (
          <div style={{ display: 'flex', gap: '8px' }}>
            <ButtonContained onClick={() => handleOpenModal(params.row)}>View</ButtonContained>
            <ButtonContained onClick={() => handleRelease(params.row.id)}>
              <ReleaseIcon /> {buttonText}
            </ButtonContained>
          </div>
        );
      }
      
    }
  ];

  return (
    <AppLayout>
      <Grid container justifyContent="center" style={{ marginTop: "20px" }}>
        <Grid item xs={11} md={11}>
          <Card
            header={
              <CardHeader
                status={`${numberOfPendingExpenses} pending expense(s)`}
                subtitleText="Approve and Release Pending Expenses"
                titleText="Employee Expense Reimbursements"
              />
            }
          >
            <Grid container spacing={2}>
              {/* Search Field */}
              <Grid item xs={12} style={{ margin: 10 }}>
                <StyledTextField
                  fullWidth
                  placeholder="Search..."
                  onChange={handleSearch}
                />
              </Grid>

              <Grid item xs={12} style={{ margin: 10, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
  {loading ? (
    <CircularProgress sx={{ marginBottom: '20px' }} />
  ) : error ? (
    <Typography variant="h6" color="error">
      {error}
    </Typography>
  ) : (
    <div style={{ width: '100%' }}>
      <DataGrid
        rows={filteredParentExpenses}
        columns={columns}
        autoHeight
        disableSelectionOnClick
        sx={{ fontFamily: "SapFiori72, sans-serif" }}
      />
    </div>
  )}
</Grid>


              {/* Dialog for Expense Details */}
              <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalStyle}>
        <IconButton
            sx={{ position: 'absolute', top: 8, right: 8 }}
            onClick={handleCloseModal}
          >
            <CloseIcon />
          </IconButton>
                {selectedParentExpense && (
                  <div>
                    <p style={{ fontFamily: "SapFiori72, sans-serif" }}><b>Employee Name:</b> {selectedParentExpense.employee_name}</p>
                    <p style={{ fontFamily: "SapFiori72, sans-serif" }}><b>Employee Position:</b> {selectedParentExpense.position_name}</p>
                    <p style={{ fontFamily: "SapFiori72, sans-serif" }}><b>Company:</b> {selectedParentExpense.company_name}</p>
                    <p style={{ fontFamily: "SapFiori72, sans-serif" }}><b>Function:</b> {selectedParentExpense.function_name}</p>
                    <p style={{ fontFamily: "SapFiori72, sans-serif" }}><b>Expense Submit Date:</b> {selectedParentExpense.expense_submit_date}</p>
                    <p style={{ fontFamily: "SapFiori72, sans-serif" }}><b>Base Currency:</b> {selectedParentExpense.base_currency}</p>
                    <p style={{ fontFamily: "SapFiori72, sans-serif" }}><b>Total Amount:</b> {selectedParentExpense.total_base_amount.toLocaleString()}</p>

                    <div style={{ overflow: 'auto' }}>

<Box sx={{ border: '2px solid #354a5f', borderRadius: '4px', marginTop: 2 }}>
<Box sx={{ overflow: 'auto' }}> {/* Added Box around the table with overflow */}
  <Table>
    <TableHead sx={{ backgroundColor: '#354a5f' }}>
      <TableRow>
      <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Expense Date</TableCell>
        <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Expense Name</TableCell>
        <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Description</TableCell>
        <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Comments</TableCell>
        <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Amount</TableCell>
        <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Currency</TableCell>
  
        
        <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif", textAlign: 'right'  }}>Base Amount</TableCell>
        <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Base Currency</TableCell>
        <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Category</TableCell>
        <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Attachment</TableCell>
        <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}></TableCell>


      </TableRow>
    </TableHead>
    <TableBody>
      {selectedParentExpense.expenses.length > 0 ? (
        selectedParentExpense.expenses.map(child => (
          <TableRow key={child.id}>
                        <TableCell>{child.expense_date}</TableCell>

            <TableCell style={{ fontFamily: "SapFiori72, sans-serif" }}>{child.expense_name}</TableCell>
            <TableCell style={{ fontFamily: "SapFiori72, sans-serif" }}>{child.expense_description || 'N/A'}</TableCell> {/* Assuming 'description' field */}
            <TableCell style={{ fontFamily: "SapFiori72, sans-serif" }}>{child.comments || 'N/A'}</TableCell>
            <TableCell style={{ fontFamily: "SapFiori72, sans-serif" }}>{child.amount}</TableCell>
            <TableCell style={{ fontFamily: "SapFiori72, sans-serif" }}>{child.currency}</TableCell>
      
            
            <TableCell sx={{ textAlign: 'right'  }}>{child.base_amount.toLocaleString() || 'N/A'}</TableCell>
            <TableCell style={{ fontFamily: "SapFiori72, sans-serif" }}>{child.base_currency}</TableCell>
            <TableCell style={{ fontFamily: "SapFiori72, sans-serif" }}>{child.expense_category_name}</TableCell>
            <TableCell>
              {/* Check for PDF or Image attachment */}
              {child.pdf_file ? (
                <ButtonOutlined onClick={() => handleOpenFile(child.pdf_file, 'application/pdf')}>
                 <AttachmentIcon />
                </ButtonOutlined>
              ) : child.image_file ? (
                <ButtonOutlined variant="contained" onClick={() => handleOpenFile(child.image_file, 'image/png')}>
                 <AttachmentIcon />
                 </ButtonOutlined>
              ) : (
                'No Attachment'
              )}
            </TableCell>

            <TableCell  style={{ fontFamily: "SapFiori72, sans-serif" }}>
                          {child.is_rejected ? ( // Check if the child expense has been rejected
                            <Button disabled>Rejected</Button>
                          ) : (
                            <ButtonContained onClick={() => handleOpenRejectDialog(child)}>Reject</ButtonContained>
                          )}
                        </TableCell>
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell colSpan={9}>No child expenses available</TableCell>
        </TableRow>
      )}
    </TableBody>
  </Table>
  </Box>
</Box>
</div>
                  </div>
                )}
              </Box>
              </Modal>


              <Dialog
        open={rejectDialogOpen}
        onClose={handleCloseRejectDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle  style={{ fontFamily: "SapFiori72, sans-serif" }} id="alert-dialog-title">
          Reject Expense
          <IconButton
            aria-label="close"
            onClick={handleCloseRejectDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedChildExpense && (
            <div>
              <p style={{ fontFamily: "SapFiori72, sans-serif" }}><b>Expense Name:</b> {selectedChildExpense.expense_name}</p>
              <p style={{ fontFamily: "SapFiori72, sans-serif" }}><b>Amount:</b> {selectedChildExpense.base_currency} {selectedChildExpense.base_amount.toLocaleString()}</p>
              <StyledTextField
                fullWidth
                label="Rejection Reason"
                multiline
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <ButtonOutlined onClick={handleCloseRejectDialog}>Cancel</ButtonOutlined>
          <ButtonContained onClick={handleRejectExpense}>Submit Rejection</ButtonContained>
        </DialogActions>
      </Dialog>


            </Grid>
          </Card>
        </Grid>
      </Grid>
    </AppLayout>
  );
};

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflow: 'auto',
};
export default ExpenseReimbursements;
